import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import mapboxgl from 'mapbox-gl'
import { setPopup } from '../../redux/slices/Map/map'
import { getPopupComponent } from './getPopupComponent'
import { useMapboxPopup } from '../../hooks/useMapboxPopup'
import { MANAGEMENT_TREE } from '../../config/routeType'
import { buildCantonUrl } from '../../utils/cantonUrl'
import { useCities } from '../../context/CitiesContext'
import minimizeMarkerImg from '../../assets/images/circle-f/tree.png'

/**
 * SOLUCIÓN CON PORTAL
 *
 * - useMapboxPopup crea el contenedor del popup inmediatamente
 * - React Portal renderiza el contenido en el contenedor
 * - Mantiene todo el control de React sobre el contenido (eventos, estado, etc)
 */
const PopupContentContainer = ({ cityName, lngLat, map, propsContent }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { treeId: urlTreeId } = useParams()

  const isDrawerOpen = useSelector(state => state.drawerRight.open)
  const popupState = useSelector(state => state.mapState.popup)
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()

  // Calculado antes del estado para usarse como valor inicial
  const contentKey =
    propsContent?.tree?.properties?.id || propsContent?.tree?.properties?.code || 'no-key'
  const isOnEditRoute = location.pathname.includes(MANAGEMENT_TREE)

  // "Derived state during render": clave que detecta cambios de árbol/popup/ruta
  // Cuando cambia, React descarta el render actual y re-renderiza con el estado correcto
  // → el popup NUNCA aparece visible antes de minimizarse (no hay flash)
  const syncKey = `${contentKey}|${popupState.open}|${isOnEditRoute}`
  const [prevSyncKey, setPrevSyncKey] = useState(syncKey)
  const [isPinned, setIsPinned] = useState(!isOnEditRoute)
  const [isMinimized, setIsMinimized] = useState(isOnEditRoute)
  const minimizeMarkerRef = useRef(null)

  if (prevSyncKey !== syncKey) {
    setPrevSyncKey(syncKey)
    setIsMinimized(isOnEditRoute)
    setIsPinned(!isOnEditRoute)
  }

  const handleTogglePin = useCallback(() => {
    setIsPinned(prev => {
      if (prev) setIsMinimized(true) // desanclar → minimizar inmediatamente
      return !prev
    })
  }, [])

  // Restaurar popup al hacer click en el marcador y volver a fijar el pin
  const handleRestore = useCallback(() => {
    setIsMinimized(false)
    setIsPinned(true)
  }, [])

  // Cuando isPinned pasa de false→true (via handleTogglePin), restaurar visibilidad
  useEffect(() => {
    if (isPinned) setIsMinimized(false)
  }, [isPinned])

  // Handler para cerrar el popup
  const handleClose = useCallback(() => {
    const isEdit = location.pathname.includes(MANAGEMENT_TREE)

    if (isEdit) {
      const popupTreeId = propsContent?.tree?.properties?.id
      const isFormTree = popupTreeId != null && String(popupTreeId) === String(urlTreeId)
      if (isFormTree) {
        // Árbol del formulario: minimizar en vez de cerrar
        setIsMinimized(true)
        setIsPinned(false)
      } else {
        // Árbol diferente: solo cerrar el popup, sin navegar
        dispatch(setPopup({ open: false, code: null, id: null }))
      }
      return
    }

    // Fuera del formulario: cerrar y navegar si corresponde
    dispatch(setPopup({ open: false, code: null, id: null }))

    const cityBase = buildCantonUrl(cityName, cantones)
    const isTreeRoute = new RegExp(`^${cityBase}(/${MANAGEMENT_TREE})?/[^/]+$`).test(
      location.pathname
    )

    if (isTreeRoute && !isDrawerOpen) {
      const base = location.pathname.includes(MANAGEMENT_TREE)
        ? buildCantonUrl(cityName, cantones, `/${MANAGEMENT_TREE}`)
        : buildCantonUrl(cityName, cantones)
      navigate(base, { replace: true })
    }
  }, [cityName, cantones, dispatch, location.pathname, navigate, isDrawerOpen, propsContent, urlTreeId])

  // Marcador personalizado cuando el popup está minimizado
  useEffect(() => {
    if (!map || !lngLat || !isMinimized) {
      if (minimizeMarkerRef.current) {
        minimizeMarkerRef.current.remove()
        minimizeMarkerRef.current = null
      }
      return
    }

    const mapContainer = map.getContainer?.()
    if (!mapContainer || !mapContainer.isConnected) return

    const el = document.createElement('img')
    el.src = minimizeMarkerImg
    el.style.width = '48px'
    el.style.height = '48px'
    el.style.cursor = 'pointer'
    el.style.display = 'block'
    el.addEventListener('click', handleRestore)

    let marker = null
    try {
      marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(lngLat)
        .addTo(map)
    } catch (_) {
      el.removeEventListener('click', handleRestore)
      return
    }

    minimizeMarkerRef.current = marker

    return () => {
      el.removeEventListener('click', handleRestore)
      marker?.remove()
      minimizeMarkerRef.current = null
    }
  }, [map, lngLat, isMinimized, handleRestore])

  // Determinar si debe renderizar contenido
  const shouldRender = Boolean(lngLat && propsContent && popupState.open)

  // Generar el componente de contenido
  const popupComponent = useMemo(() => {
    if (!shouldRender) return null

    return getPopupComponent({
      pathname: location.pathname,
      propsContent: {
        ...propsContent,
        lngLat,
        closePopup: handleClose,
        closePopupWithNavigation: handleClose,
        isPinned,
        onTogglePin: handleTogglePin,
      },
      lngLat,
    })
  }, [
    shouldRender,
    location.pathname,
    propsContent,
    lngLat,
    handleClose,
    isPinned,
    handleTogglePin,
  ])

  // Crear el popup usando el hook
  const popupContainerElement = useMapboxPopup({
    map,
    lngLat,
    contentKey,
    isOpen: popupState.open,
    isMinimized,
    autoClose: false,
    maxWidth: '27em',
  })

  if (!shouldRender) return null

  if (popupContainerElement) {
    return createPortal(popupComponent, popupContainerElement)
  }

  return null
}

export default React.memo(PopupContentContainer)
